import type {FetchResult, ObservableQuery} from '@apollo/client';
import type {SignupStepInputInput} from 'gql/types';

import type {PerformSignupStepMutation} from './signUp.queries.generated';
import {usePerformSignupStepMutation} from './signUp.queries.generated';

export type SignupStepResult = FetchResult<PerformSignupStepMutation> & {
  /** False when the step committed but `currentUserPendingTasks` could not be refreshed. */
  pendingTasksRefreshed: boolean;
};

/**
 * Performs one signup step, then refreshes `currentUserPendingTasks` before resolving so
 * callers route on fresh pending tasks. A failed refresh never rejects, because the step has
 * already committed: it is logged, ForceOnboardingGate reports its own copy of the query
 * error, and `pendingTasksRefreshed` lets callers that route off the refreshed tasks surface
 * the miss.
 */
export function usePerformSignupStep() {
  const [performSignupStep] = usePerformSignupStepMutation();
  return async (step: SignupStepInputInput): Promise<SignupStepResult> => {
    let pendingTasksRefreshed = true;
    const result = await performSignupStep({
      variables: {input: {input: step}},
      refetchQueries: ['currentUserPendingTasks'],
      onQueryUpdated: (query: ObservableQuery) =>
        query.refetch().catch((error: unknown) => {
          pendingTasksRefreshed = false;
          logger.warn(
            'currentUserPendingTasks refresh failed after a committed signup step: %o',
            error
          );
        })
    });
    return {...result, pendingTasksRefreshed};
  };
}
